import React, { useContext, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import MyDatePicker from "../../common/components/MyDatePicker";
import { gql, useMutation, useQuery } from "@apollo/client";
import Swal from "sweetalert2";
import clientGraphql from "../../../common/apollo-graphql";
import ClinicDropdown from "../../common/components/Clinics/ClinicDropdown";
import moment from "moment";
import { MyTypography } from "../../common/components";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ProviderDropdown from "../../common/components/Providers/ProviderDropdown";
import AuthContext from "../../auth/context/AuthContext";

const schema = yup.object().shape({
  firstname: yup.string().trim().required("First Name is required"),
  lastname: yup.string().trim().required("Last Name is required"),
  mrn: yup.string().nullable(),
  birthdate: yup.date().nullable(),
  diagnosis: yup.string().nullable(),
  clinic_referrer_id: yup.number(),
  provider_id: yup.number(),
  clinic_receiver_id: yup.number(),
  insurance_provider: yup.string(),
  policy_number: yup.string(),
  insurance_provider2: yup.string(),
  policy_number2: yup.string(),
  sentdate: yup.date().required("Date is required"),
  is_order: yup.boolean(),
});

const CREATE_REFERRAL = gql`
  mutation CreateReferral($params: String!) {
    save_referral(params: $params)
  }
`;

export const GET_REFERRAL_BY_UUID = gql`
  query GetReferral($uuid: String!) {
    referral(uuid: $uuid) {
      is_order
      sent_at
      status
      mrn

      diagnosis {
        description
      }
      receiver {
        id
      }
      referrer {
        id
      }
      provider {
        id
      }
      patient {
        firstname
        lastname
        details {
          birthdate
          insurance_provider
          policy_number
          insurance_provider2
          policy_number2
        }
      }
    }
  }
`;

const ReferralCreateForm = ({ onClose, referralUuid = null, fromReceived = false }) => {
  const currentUser = useContext(AuthContext);
  const isEditing = Boolean(referralUuid);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      mrn: "",
      birthdate: null,
      diagnosis: "",
      clinic_referrer_id: fromReceived ? 0 : currentUser?.clinics?.[0]?.id,
      clinic_receiver_id: 0,
      provider_id: fromReceived ? currentUser?.provider_id : 0,
      insurance_provider: "",
      policy_number: "",
      insurance_provider2: "",
      policy_number2: "",
      sentdate: moment().format("YYYY-MM-DD"),
      is_order: false,
    },
    resolver: yupResolver(schema),
  });

  const watchedClinicReceiverId = useWatch({ control, name: 'clinic_receiver_id' });
  const clinicReceiverId = fromReceived
    ? currentUser?.clinics?.[0]?.id
    : watchedClinicReceiverId;

  const { data: referralData, loading: loadingReferral, refetch } = useQuery(
    GET_REFERRAL_BY_UUID,
    {
      client: clientGraphql,
      variables: { uuid: referralUuid },
      skip: !referralUuid,
    }
  );

  const [saveReferral, { loading }] = useMutation(CREATE_REFERRAL, {
    client: clientGraphql,
  });

  const onSubmit = async (data) => {
    let type = `${data.is_order ? "Order" : "Referral"}`;
    try {
      const payload = {
        ...data,
        clinic_receiver_id: clinicReceiverId,
        birthdate: data.birthdate ? moment(data.birthdate).format("YYYY-MM-DD") : null,
        sentdate: data.sentdate ? moment(data.sentdate).format("YYYY-MM-DD") : null,
        uuid: referralUuid,
      };

      await saveReferral({
        variables: { params: JSON.stringify(payload) },
      });

      Swal.fire({
        icon: "success",
        text: `${type} ${isEditing ? "updated" : "created"}!`,
        showConfirmButton: true,
      });

      reset();
      onClose();
    } catch (error) {
      const graphqlError = error?.graphQLErrors?.[0];
      const message =
        graphqlError?.debugMessage ||
        graphqlError?.message ||
        `Error ${isEditing ? "updating" : "creating"} ${type}!`;
      Swal.fire({ icon: "error", text: message, showConfirmButton: true });
    }
  };

  useEffect(() => {
    if (referralUuid) refetch();
  }, [referralUuid, refetch]);

  useEffect(() => {
    if (referralData?.referral) {
      const referral = referralData.referral;
      const patient = referral.patient || {};
      const details = patient.details || {};

      reset({
        firstname: patient.firstname || "",
        lastname: patient.lastname || "",
        mrn: referral.mrn || "",
        clinic_receiver_id: referral.receiver?.id || 0,
        clinic_referrer_id: referral.referrer?.id || 0,
        provider_id: referral.provider?.id || 0,
        birthdate: details.birthdate ? moment(details.birthdate).toDate() : null,
        diagnosis: referral.diagnosis?.description || "",
        insurance_provider: details.insurance_provider || "",
        policy_number: details.policy_number || "",
        insurance_provider2: details.insurance_provider2 || "",
        policy_number2: details.policy_number2 || "",
        sentdate: referral.sent_at ? moment(referral.sent_at).toDate() : moment().toDate(),
        is_order: referral.is_order || false,
      });
    }
  }, [referralData, reset]);

  if (loadingReferral) return <div>Loading referral...</div>;

  return (
    <Box mt={3}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstname"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  required
                  error={!!errors.firstname}
                  helperText={errors.firstname?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastname"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  required
                  error={!!errors.lastname}
                  helperText={errors.lastname?.message}
                />
              )}
            />
          </Grid>
          {!fromReceived && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="mrn"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="MRN" fullWidth />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={fromReceived ? 12 : 6}>
            <Controller
              name="birthdate"
              control={control}
              render={({ field }) => (
                <MyDatePicker
                  label="Birth Date"
                  value={field.value}
                  onChange={field.onChange}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="diagnosis"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Diagnosis" fullWidth />
              )}
            />
          </Grid>
          {!fromReceived && (
            <Grid item xs={12}>
              <Controller
                name="clinic_receiver_id"
                control={control}
                render={({ field }) => (
                  <ClinicDropdown
                    label="Clinic Receiver"
                    value={field.value}
                    forFax={false}
                    onChange={field.onChange}
                    error={!!errors.clinic_receiver_id}
                    helperText={errors.clinic_receiver_id?.message}
                  />
                )}
              />
            </Grid>
          )}
          {fromReceived && (
            <Grid item xs={12}>
              <Controller
                name="clinic_referrer_id"
                control={control}
                render={({ field }) => (
                  <ClinicDropdown
                    label="Clinic Sender"
                    value={field.value}
                    forFax={false}
                    onChange={field.onChange}
                    error={!!errors.clinic_referrer_id}
                    helperText={errors.clinic_referrer_id?.message}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Controller
              name="provider_id"
              control={control}
              render={({ field }) => (
                <ProviderDropdown
                  clinic={clinicReceiverId ? { id: clinicReceiverId } : null}
                  label="Provider Receiver"
                  value={field.value}
                  forFax={true}
                  onChange={field.onChange}
                  error={!!errors.provider_id}
                  helperText={errors.provider_id?.message}
                  isReceiver={false}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="insurance_provider"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Insurance Provider"
                  fullWidth
                  error={!!errors.insurance_provider}
                  helperText={errors.insurance_provider?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="policy_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Policy Number"
                  fullWidth
                  error={!!errors.policy_number}
                  helperText={errors.policy_number?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="insurance_provider2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Secondary Insurance Provider"
                  fullWidth
                  error={!!errors.insurance_provider2}
                  helperText={errors.insurance_provider2?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="policy_number2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Secondary Policy Number"
                  fullWidth
                  error={!!errors.policy_number2}
                  helperText={errors.policy_number2?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="sentdate"
              control={control}
              render={({ field }) => (
                <MyDatePicker
                  label={fromReceived ? "Received Date" : "Sent Date"}
                  value={field.value}
                  onChange={field.onChange}
                  fullWidth
                />
              )}
            />
            {errors.sentdate && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.sentdate.message}
              </span>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="is_order"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label={
                    <MyTypography variant="body2">
                      Please check this box if this is an <b>Order</b>
                    </MyTypography>
                  }
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button onClick={onClose} color="inherit" sx={{ mr: 2 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ReferralCreateForm;
